import React, { FC, ReactNode, useState } from 'react';
import { DocumentNode } from 'graphql';
import { Query, QueryResult, QueryComponentOptions } from 'react-apollo';
import { FetchMoreOptions } from 'apollo-client';
import { Subtract } from 'utility-types';

import { PublicUserProfile, Group, Content, QuerySearchArgs, UserProfile } from 'api';
import { LoaderTypes } from 'types/app';
import { QueryManager } from 'components';

export interface ISearchQuery {
  loading: boolean;
  exhausted: boolean;
  count: number;
  results: Array<UserProfile & PublicUserProfile & Group & Content>;
  onLoadMore: () => void;
}

interface ISubtracted {
  query: QueryComponentOptions['query'];
  children: QueryComponentOptions['children'];
}

type TQueryProps = Subtract<QueryComponentOptions, ISubtracted>;

interface IProps extends TQueryProps {
  document: DocumentNode;
  variables: Partial<QuerySearchArgs>;
  children: (props: ISearchQuery) => ReactNode;
  loader?: LoaderTypes;
}

const Search: FC<IProps> = props => {
  const [loading, setLoading] = useState(false);
  const { document, variables, loader, children, ...queryProps } = props;

  const updateQuery: FetchMoreOptions['updateQuery'] = (prev, options) => {
    const { fetchMoreResult } = options;

    setLoading(false);

    if (!fetchMoreResult) {
      return prev;
    }

    return {
      ...prev,
      search: {
        ...prev.search,
        results_from: fetchMoreResult.search.results_from,
        results: [...prev.search.results, ...fetchMoreResult.search.results],
      },
    };
  };

  function onLoadMore(queryResult: QueryResult) {
    const { data, fetchMore } = queryResult;

    setLoading(true);

    return fetchMore({
      updateQuery,
      variables: {
        results_from: data.search.results.length,
      },
    });
  }

  function hasSearch(data: QueryResult['data']) {
    return data && data.search;
  }

  return (
    <Query query={document} variables={variables} {...queryProps}>
      {(queryResult: QueryResult) => (
        <QueryManager loader={loader} {...queryResult}>
          {({ data }) => {
            if (!hasSearch(data)) {
              return null;
            }

            const { search } = data;
            const exhausted = search.results.length >= search.count;

            return children({
              exhausted,
              loading,
              count: search.count,
              results: search.results,
              onLoadMore: () => onLoadMore(queryResult),
            });
          }}
        </QueryManager>
      )}
    </Query>
  );
};

Search.defaultProps = {
  loader: 'standard',
};

export default Search;
